import React from 'react';

interface TermsOfServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContactClick?: () => void;
  onPrivacyClick?: () => void;
}

const TermsOfServiceModal: React.FC<TermsOfServiceModalProps> = ({ isOpen, onClose, onContactClick, onPrivacyClick }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="rounded-lg shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        style={{ backgroundColor: '#161616' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div>
            <h2 className="text-2xl font-bold text-white">Terms of Service</h2>
            <p className="text-gray-400 text-sm mt-1">Effective Date: November 21, 2025</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors text-2xl"
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6 text-gray-300">
          <div className="bg-blue-900/20 border border-blue-500/30 p-4 rounded-lg mb-6">
            <strong className="text-blue-300">Summary:</strong>
            <span className="text-gray-300"> By using Chord Notator, you agree to these terms. Please read them carefully before using our service.</span>
          </div>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">1. Acceptance of Terms</h3>
            <p className="mb-3">By accessing or using Chord Notator ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service.</p>
            <p>We reserve the right to modify these Terms at any time. Your continued use of the Service after any changes indicates your acceptance of the new Terms.</p>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">2. Description of Service</h3>
            <p className="mb-3">Chord Notator is a web-based application that allows musicians to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Create and edit chord charts for songs</li>
              <li>Store and organize musical arrangements</li>
              <li>Sync charts across devices using Google Drive</li>
              <li>Collaborate with other musicians through Jam Sessions</li>
              <li>Access premium features through paid subscriptions</li>
            </ul>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">3. User Accounts</h3>
            
            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">3.1 Account Creation</h4>
            <p className="mb-3">To access certain features, you must sign in using your Google account. You are responsible for:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Maintaining the security of your account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized use</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">3.2 Account Termination</h4>
            <p>We reserve the right to suspend or terminate your account if you violate these Terms or engage in fraudulent or illegal activities.</p>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">4. User Content</h3>
            
            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">4.1 Ownership</h4>
            <p className="mb-3">You retain ownership of all chord charts, lyrics, and other content you create using Chord Notator ("User Content"). We do not claim any ownership rights over your User Content.</p>

            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">4.2 License to Us</h4>
            <p className="mb-3">By using the Service, you grant us a limited license to store, process, and display your User Content solely for the purpose of providing the Service to you.</p>

            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">4.3 Your Responsibilities</h4>
            <p className="mb-3">You are solely responsible for:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Ensuring you have the rights to any content you upload (including lyrics and musical arrangements)</li>
              <li>Not uploading content that infringes on third-party copyrights or intellectual property</li>
              <li>Complying with all applicable copyright laws</li>
            </ul>

            <div className="bg-yellow-900/20 border border-yellow-500/30 p-4 rounded-lg mt-4">
              <strong className="text-yellow-300">Copyright Notice:</strong>
              <span className="text-gray-300"> Chord Notator is designed for personal use and creating original arrangements. Users are responsible for ensuring their use complies with copyright laws in their jurisdiction.</span>
            </div>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">5. Acceptable Use</h3>
            <p className="mb-3">You agree NOT to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Service for any illegal purpose</li>
              <li>Attempt to gain unauthorized access to the Service or its systems</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Upload malicious code or harmful content</li>
              <li>Impersonate another person or entity</li>
              <li>Use the Service to spam or harass others</li>
              <li>Reverse engineer or attempt to extract the source code</li>
              <li>Resell or redistribute the Service without authorization</li>
            </ul>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">6. Premium Subscriptions</h3>
            
            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">6.1 Billing</h4>
            <ul className="list-disc pl-6 space-y-2">
              <li>Premium features require a paid subscription</li>
              <li>Subscriptions are billed through Stripe</li>
              <li>Prices are displayed before purchase and may change with notice</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">6.2 Cancellation</h4>
            <ul className="list-disc pl-6 space-y-2">
              <li>You may cancel your subscription at any time</li>
              <li>Cancellation takes effect at the end of the current billing period</li>
              <li>No refunds are provided for partial billing periods</li>
            </ul>

            <h4 className="text-lg font-semibold text-gray-200 mt-4 mb-2">6.3 Free Features</h4>
            <p>Basic features of Chord Notator remain free. Free users may have limitations on storage, features, or usage.</p>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">7. Third-Party Services</h3>
            <p className="mb-3">Chord Notator integrates with third-party services:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-gray-200">Google:</strong> For authentication and Drive storage (subject to Google's Terms of Service)</li>
              <li><strong className="text-gray-200">Stripe:</strong> For payment processing (subject to Stripe's Terms of Service)</li>
            </ul>
            <p className="mt-3">We are not responsible for the practices or content of third-party services.</p>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">8. Intellectual Property</h3>
            <p className="mb-3">The Chord Notator name, logo, and all related trademarks, service marks, and graphics are our property. You may not use them without our prior written consent.</p>
            <p>The Service's design, features, and functionality are protected by copyright, trademark, and other intellectual property laws.</p>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">9. Disclaimer of Warranties</h3>
            <div className="bg-yellow-900/20 border border-yellow-500/30 p-4 rounded-lg mb-4">
              <strong className="text-yellow-300">Important:</strong>
              <span className="text-gray-300"> The Service is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, either express or implied.</span>
            </div>
            <p className="mb-3">We do not warrant that:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The Service will be uninterrupted or error-free</li>
              <li>Defects will be corrected</li>
              <li>The Service is free of viruses or harmful components</li>
              <li>The results of using the Service will meet your requirements</li>
            </ul>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">10. Limitation of Liability</h3>
            <p className="mb-3">To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Loss of data or content</li>
              <li>Loss of profits or revenue</li>
              <li>Business interruption</li>
              <li>Any damages arising from your use of the Service</li>
            </ul>
            <p className="mt-3">Our total liability shall not exceed the amount you paid us in the twelve (12) months preceding the claim.</p>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">11. Indemnification</h3>
            <p className="mb-3">You agree to indemnify and hold harmless Chord Notator and its operators from any claims, damages, losses, or expenses arising from:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Any content you upload to the Service</li>
            </ul>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">12. Governing Law</h3>
            <p className="mb-3">These Terms shall be governed by and construed in accordance with the laws of France, without regard to its conflict of law provisions.</p>
            <p>Any disputes arising from these Terms or the Service shall be resolved in the courts of France.</p>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">13. Severability</h3>
            <p>If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.</p>
          </section>

          <section className="mb-6">
            <h3 className="text-xl font-bold text-white mb-3">14. Entire Agreement</h3>
            <p>These Terms, together with our{' '}
              <button
                onClick={() => {
                  onPrivacyClick?.();
                  onClose();
                }}
                className="text-cyan-400 hover:text-cyan-300 underline"
              >
                Privacy Policy
              </button>
              , constitute the entire agreement between you and Chord Notator regarding the Service.
            </p>
          </section>

          <section className="mb-6 bg-gray-800/50 p-4 rounded-lg">
            <h3 className="text-xl font-bold text-white mb-3">15. Contact Us</h3>
            <p className="mb-3">If you have any questions about these Terms of Service, please contact us:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <strong className="text-gray-200">Contact:</strong>{' '}
                <button
                  onClick={() => {
                    onContactClick?.();
                    onClose();
                  }}
                  className="text-cyan-400 hover:text-cyan-300 underline"
                >
                  Click here to contact us
                </button>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfServiceModal;
